// ゼンリン地図系 API（search / reverse / reverse-latlng / reverse-circle / reverse-polygon）の
// 利用者単位レート制限（HTTP 429）を、通常の「データなし」と区別して扱うためのユーティリティ。

import { isAxiosError } from "axios";
import { toast } from "react-toastify";

export const ZENRIN_RATE_LIMIT_BASE_MESSAGE =
  "リクエストが集中しています。しばらくしてから再度お試しください。";

/** 連続して 429 になってもトーストが積み重ならないよう、同一 ID で 1 つにまとめる */
export const ZENRIN_RATE_LIMIT_TOAST_ID = "zenrin-rate-limit";

export class ZenrinRateLimitError extends Error {
  readonly retryAfterSeconds: number | undefined;

  constructor(retryAfterSeconds?: number) {
    super(ZENRIN_RATE_LIMIT_BASE_MESSAGE);
    this.name = "ZenrinRateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
    // ES5 ターゲットでも instanceof を成立させる
    Object.setPrototypeOf(this, ZenrinRateLimitError.prototype);
  }
}

const parseRetryAfterSeconds = (value: unknown): number | undefined => {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0
    ? Math.ceil(seconds)
    : undefined;
};

/** axios エラーが HTTP 429 の場合のみ ZenrinRateLimitError に変換する。それ以外は undefined */
export const toZenrinRateLimitError = (
  error: unknown
): ZenrinRateLimitError | undefined => {
  if (!isAxiosError(error)) return undefined;
  const response = error.response;
  if (response?.status !== 429) return undefined;

  // ヘッダを優先。CORS 等で読めない場合は body を使う。
  // apiClient は axios-case-converter により snake_case を camelCase にするので両方見る。
  const fromHeader = parseRetryAfterSeconds(response.headers?.["retry-after"]);
  const body =
    response.data !== null &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
      ? (response.data as Record<string, unknown>)
      : undefined;
  const fromBody = parseRetryAfterSeconds(
    body?.retry_after ?? body?.retryAfter
  );

  return new ZenrinRateLimitError(fromHeader ?? fromBody);
};
/** 429 のときだけ ZenrinRateLimitError を throw する。それ以外は何もしない（従来どおり握りつぶす用） */
export const throwIfZenrinRateLimited = (error: unknown): void => {
  const rateLimitError = toZenrinRateLimitError(error);
  if (rateLimitError) throw rateLimitError;
};

export const isZenrinRateLimitError = (
  error: unknown
): error is ZenrinRateLimitError => error instanceof ZenrinRateLimitError;

/**
 * 429 ならレート制限トーストを出して true を返す。
 * true のとき呼び出し側は「データなし」等のトーストを出さないこと。
 */
export const consumeZenrinRateLimitError = (error: unknown): boolean => {
  if (!isZenrinRateLimitError(error)) return false;
  showZenrinRateLimitToast(error);
  return true;
};

/** 再試行までの目安を人が読める形にする（例: 約45秒後 / 約3分後 / 約2時間後） */
export const formatRetryAfter = (seconds: number): string => {
  if (seconds < 60) return `約${seconds}秒後`;
  if (seconds < 60 * 60) return `約${Math.ceil(seconds / 60)}分後`;
  return `約${Math.ceil(seconds / (60 * 60))}時間後`;
};

export const buildZenrinRateLimitMessage = (
  retryAfterSeconds?: number
): string =>
  retryAfterSeconds === undefined
    ? ZENRIN_RATE_LIMIT_BASE_MESSAGE
    : `${ZENRIN_RATE_LIMIT_BASE_MESSAGE}（再試行の目安: ${formatRetryAfter(
        retryAfterSeconds
      )}）`;

export const showZenrinRateLimitToast = (error: ZenrinRateLimitError): void => {
  toast.error(buildZenrinRateLimitMessage(error.retryAfterSeconds), {
    toastId: ZENRIN_RATE_LIMIT_TOAST_ID,
  });
};

/**
 * RPS 系の短い窓（概ね 1 秒）なら待って続行できる。
 * RPM 系（数十秒〜）は UI を長時間止めるので待たず打ち切る。
 */
export const ZENRIN_SHORT_RETRY_AFTER_SECONDS = 1;

export const shouldWaitAndRetryZenrinRateLimit = (
  retryAfterSeconds: number | undefined
): boolean =>
  retryAfterSeconds !== undefined &&
  retryAfterSeconds > 0 &&
  retryAfterSeconds <= ZENRIN_SHORT_RETRY_AFTER_SECONDS;

export const sleepMs = async (ms: number): Promise<void> => {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
};

/**
 * 受付帳からゼンリン地図へプロットするとき、同時に送る地番検索の件数。
 * 直列（1）では 500 件で 3〜4 分かかったため、並列にしている。引き上げるときは次を確認すること。
 * - BE の受付帳プロット専用枠（1 秒あたり。ZENRIN_RECEPTION_BOOK_PLOT_RATE_RPS）とゼンリン側の秒間上限に収まる
 *   （1 件 0.3〜0.5 秒なので、同時送信数 N で毎秒おおよそ 2N〜3N 件）
 * - 429 の待機はワーカー間で共有している（getAllPopupReceptionBooks の resumeAt）
 * - BE の受付帳プロット用スロットルは、同じユーザーの並列リクエストがロックの取り合いで 429 にならないよう短く待つ
 */
export const ZENRIN_RECEPTION_BOOK_PLOT_CONCURRENCY = 3;
